import emoji from 'node-emoji';
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AppView } from 'design-system/AppView';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import StatusBar from 'design-system/StatusBar';
import { styles as textStyles } from 'design-system/Text';
import TextInput from 'utils/packages/TextInput';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import AlertWeb from 'utils/packages/Alert';
import { useAppSelector } from 'store/hooks';
import { selectShowNewPhysicalAssets } from 'features/accounts/selectors';
import { Sign } from 'utils/types/navigation';
import { isYesterdayOrOlder } from 'utils/date/resetDay';
import { KeyboardAvoidingContainerNew } from 'design-system/KeyboardAvoidingContainerNew';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { isWeb } from '../../../constants';
import { selectShouldShowCsvImports } from '../hooks/selectors';
import { getPropertyName, PHYSICAL_ASSET_TYPES } from '../constants';
const strings = {
    twitterTitle: 'X Handle',
    twitterPlaceholder: '@X_handle',
    next: 'Next',
    errorTitle: 'Hold on :dizzy:',
    errorMessage: 'You must enter a X handle.',
};
const DummyAccountLogoScreen = ({ navigation, route }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const shouldShowCsvImports = useAppSelector(selectShouldShowCsvImports);
    useLayoutEffect(() => {
        navigation.setOptions({
            title: strings.twitterTitle,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const [value, setValue] = useState('');
    const textInputRef = useRef(null);
    useAutoFocusInput(textInputRef);
    const showNewPhysicalAssets = useAppSelector(selectShowNewPhysicalAssets);
    const onAddValuation = useCallback((routeParams) => {
        navigation.navigateDeprecated('SelectDate', {
            longTitle: `When did you buy this ${getPropertyName(routeParams.type).toLowerCase()}?`,
            buttonText: 'Continue',
            onSubmitDate: (date) => {
                const isPastDate = isYesterdayOrOlder(date);
                const title = isPastDate ? 'What was the initial value?' : 'What is the current value?';
                navigation.navigateDeprecated('AddBalance', {
                    ...routeParams,
                    purchaseDate: date,
                    custom: {
                        title,
                        webSubtitle: title,
                        button: 'Continue',
                        initialSign: Sign.Positive,
                    },
                    ...(isPastDate && {
                        onContinue: (amount, currency) => {
                            navigation.navigateDeprecated('HasValuationChanged', {
                                ...routeParams,
                                purchaseDate: date,
                                initialValue: amount,
                                currency,
                            });
                        },
                    }),
                });
            },
        });
    }, []);
    const onSubmit = useCallback(async () => {
        if (value.length < 2) {
            AlertWeb.alert(emoji.emojify(strings.errorTitle), strings.errorMessage);
            return;
        }
        const { params } = route;
        const routeParams = {
            ...params,
            iconProvider: 'TWITTER',
            iconProviderHandle: value,
        };
        const type = params?.type;
        const isPhysicalAsset = PHYSICAL_ASSET_TYPES.has(type);
        if (params?.isChangingLogo) {
            navigation.navigateDeprecated('DummyAccountCreated', {
                ...routeParams,
            });
        }
        else if (isPhysicalAsset && showNewPhysicalAssets) {
            onAddValuation(routeParams);
        }
        else if (shouldShowCsvImports && !isPhysicalAsset) {
            navigation.navigateDeprecated('DummyAccountCreationImport', {
                ...routeParams,
            });
        }
        else {
            navigation.navigateDeprecated('AddBalance', {
                ...routeParams,
            });
        }
    }, [value, route, showNewPhysicalAssets, shouldShowCsvImports, onAddValuation]);
    const onChangeText = useCallback((text) => {
        const sanitized = text.replace(/\s/g, '');
        setValue((sanitized.charAt(0) === '@' ? sanitized : `@${sanitized}`).toLowerCase());
    }, []);
    useAutoFocusInput(textInputRef);
    const textInput = useMemo(() => (<TextInput ref={textInputRef} style={styles.textInput} value={value} placeholder={strings.twitterPlaceholder} placeholderTextColor={colors.text.placeholder} selectionColor={colors.text.selection} underlineColorAndroid="transparent" textAlign="center" autoCapitalize="none" onChangeText={onChangeText} onSubmitEditing={onSubmit} focusStyle={styles.textInputFocus}/>), [colors, styles, value, onChangeText, onSubmit]);
    return (<AppView type={isWeb ? 'secondary' : 'primary'} isInTab={isWeb} avoidHeader>
      <StatusBar barStyle="dark-content" animated/>
      <KeyboardAvoidingContainerNew flexContainer withButtonSpace>
        {isWeb ? <Spacer h={72}/> : <Spacer flex/>}
        {textInput}
        <Spacer flex/>
      </KeyboardAvoidingContainerNew>
      <KeyboardAvoidingButton brand title={strings.next} onPress={onSubmit} withFrame={false}/>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    textInputFocus: {
        color: colors.text.primary,
    },
    textInput: {
        ...(isWeb
            ? {
                paddingVertical: rem(16),
                color: colors.text.secondary,
            }
            : {
                ...textStyles.TextThin_20,
                alignSelf: 'stretch',
                color: colors.text.primary,
                fontSize: rem(26),
                lineHeight: undefined,
            }),
    },
}));
export default DummyAccountLogoScreen;
